import React, { FC, useRef, useState } from 'react'
import { Typography } from '@mui/material'
import { Formik, FormikProps } from 'formik'

import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { DialogActions } from '@e3dc-react/shell/Elements/Dialog/Dialog'
import Form from './Form'
import { GrantCompanyAccessFormData, useCreateAccountLink } from '../../../../hooks'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { ApiAccountRole } from 'src/Libs/api'
import { useGrantCompanyAccessFormInitializer } from 'src/Libs/installation-details/hooks'

export interface GrantCompanyAccessModalProps {
  onClose?: () => void
  installationId: string
  refetchAttachedCompanies: () => void
}

/**
 * Modal to grant access to installation for a company
 * @param onClose - function to close modal
 * @param installationId - installation id
 * @param refetchAttachedCompanies - function to refetch attached companies
 */
const GrantCompanyAccessModal: FC<GrantCompanyAccessModalProps> = ({ onClose, installationId, refetchAttachedCompanies }) => {
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()

  const onCreateAccountError = (): void => {
    enqueueErrorStack(t('installation.details.info.grant-access.error-message'))
  }

  const { mutate: createAccountLink } = useCreateAccountLink({
    onSuccess: () => {
      enqueueSuccessStack(t('installation.details.info.grant-access.success-message'))
      refetchAttachedCompanies()
      onClose?.()
    },
    onError: onCreateAccountError,
  })
  const [isFormValid, setIsFormValid] = useState<boolean>(true)
  const { initialValues, validationSchema } = useGrantCompanyAccessFormInitializer(installationId)
  const formRef = useRef<FormikProps<GrantCompanyAccessFormData>>(null)
  const { t } = useCustomTranslation()

  const onSaveButtonHandler = (): void => {
    formRef.current?.handleSubmit()
  }

  const closeAction: DialogActions = {
    primary: {
      name: t('installation.details.info.grant-access.submit-button-title'),
      onClick: onSaveButtonHandler,
      disabled: !isFormValid,
    },
    secondary: [
      {
        name: t('installation.details.info.grant-access.cancel-button-title'),
        onClick: () => onClose?.(),
      },
    ],
  }

  const onSubmitForm = ({ company, reason, writePermission }: GrantCompanyAccessFormData): void => {
    const permissionType = writePermission ? 'rw' : 'ro'

    if (company === undefined || company === '') {
      onCreateAccountError()
      return
    }
    const accountId = company.toString()

    createAccountLink({
      installationId: Number.parseInt(installationId),
      payload: {
        accountId,
        accountSource: 'e3dc',
        accountType: 'company',
        role: 'installer' as ApiAccountRole,
        permissionType,
        storagePermissionType: permissionType,
        reason,
      },
    })
  }

  return (
    <Dialog title={t('installation.details.info.grant-access.dialog-title')} actions={closeAction} maxWidth="sm" disableClose>
      <Typography variant="subtitle2" mt={4} mb={4}>
        {t('installation.details.info.grant-access.dialog-text')}
      </Typography>
      <Formik initialValues={initialValues} onSubmit={onSubmitForm} validationSchema={validationSchema} innerRef={formRef} enableReinitialize>
        <Form onChangeFormValid={setIsFormValid} />
      </Formik>
    </Dialog>
  )
}

export default GrantCompanyAccessModal
